import React, { useState, useEffect, useRef, useMemo } from "react";
import DataTable from "../DataTable";
import { Link } from "react-router-dom";
import "../../index.css";
import Sidebar from "../Sidebar";
import Layout from "../Layout";
import EditInventory from "./EditInventory";
import { gql, useMutation, useQuery } from "@apollo/client";
import Loader from "../../Loader";
import Barcode from "react-barcode";
import * as htmlToImage from "html-to-image";
import download from "downloadjs";
import { useAuthz } from "../../auth/useAuthz";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  flexRender,
} from "@tanstack/react-table";

const DELETE_INVENTORY = gql`
  mutation deleteInventory($data: ID!) {
    deleteInventory(data: $data) {
      status
      message
    }
  }
`;

const GET_INVENTORY = gql`
  query GetInventory($data: getInventoryInput) {
    getInventory(data: $data) {
      message
      status
      inventoryList {
        id
        name
        category
        assignedDate
        usage
        employeeId
        status
        employeeName
        description
        printedCount
        quantity
      }
    }
  }
`;

const UPDATE_INVENTORY = gql`
  mutation updateInventory($data: updateInventoryInput!) {
    updateInventory(data: $data) {
      status
      message
    }
  }
`;

// Label component inside same file
const InventoryLabel = React.forwardRef(
  ({ logoUrl, code, description }, ref) => {
    return (
      // <div
      //   ref={ref}
      //   style={{
      //     width: "400px",
      //     padding: "12px",
      //     border: "1px solid #ccc",
      //     borderRadius: "6px",
      //     fontFamily: "Arial, sans-serif",
      //     backgroundColor: "white",
      //     textAlign: "center",
      //     display: "flex",
      //     flexDirection: "column",
      //     alignItems: "center",
      //   }}
      // >
      //   {logoUrl && (
      //     <div
      //       style={{
      //         width: "100%",
      //         display: "flex",
      //         justifyContent: "center",
      //         marginBottom: "8px",
      //       }}
      //     >
      //       <img
      //         src={logoUrl}
      //         alt="Barbera"
      //         style={{
      //           height: "40px",
      //           maxWidth: "150px",
      //           objectFit: "contain",
      //         }}
      //       />
      //     </div>
      //   )}

      //   <div
      //     style={{ fontSize: "18px", fontWeight: "bold", marginBottom: "4px" }}
      //   >
      //     {code}
      //   </div>

      //   {description && (
      //     <div style={{ fontSize: "12px", marginBottom: "8px", color: "#555" }}>
      //       {description}
      //     </div>
      //   )}

      //   <div>
      //     <Barcode value={code} height={40} fontSize={10} displayValue={true} />
      //   </div>
      // </div>
      <div className="print-label">
        {logoUrl && (
          <img
            src={logoUrl}
            alt="Logo"
            style={{ height: "5mm", marginBottom: "1mm", objectFit: "contain" }}
          />
        )}

        <div className="code">{code}</div>

        <div className="barcode-container">
          <Barcode
            value={code}
            height={22} /* Adjusted to fit new 4-label grid */
            width={1.2} /* Slightly wider width to fit 45mm cells */
            fontSize={9}
            margin={0}
            displayValue={false}
          />
        </div>
      </div>
    );
  },
);
const InventoryLabel2 = React.forwardRef(
  ({ logoUrl, code, description }, ref) => {
    return (
      <div
        ref={ref}
        style={{
          width: "400px",
          padding: "12px",
          border: "1px solid #ccc",
          borderRadius: "6px",
          fontFamily: "Arial, sans-serif",
          backgroundColor: "white",
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {logoUrl && (
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              marginBottom: "8px",
            }}
          >
            <img
              src={logoUrl}
              alt="Barbera"
              style={{
                height: "40px",
                maxWidth: "150px",
                objectFit: "contain",
              }}
            />
          </div>
        )}

        <div
          style={{ fontSize: "18px", fontWeight: "bold", marginBottom: "4px" }}
        >
          {code}
        </div>

        {description && (
          <div style={{ fontSize: "12px", marginBottom: "8px", color: "#555" }}>
            {description}
          </div>
        )}

        <div>
          <Barcode value={code} height={40} fontSize={10} displayValue={true} />
        </div>
      </div>
    );
  },
);

const InventoryTable = () => {
  const { can } = useAuthz();
  const { loading, error, data } = useQuery(GET_INVENTORY, {
    variables: { data: { onlyUsable: true } },
  });
  const [dataList, setDataList] = useState([]);
  const [isFetched, setIsFetched] = useState(false);

  const [deleteInventory, { loading: prodDeletLoading }] =
    useMutation(DELETE_INVENTORY);

  const [updateInventory] = useMutation(UPDATE_INVENTORY);

  const [editForm, setEditForm] = useState(false);
  const [editData, setEditData] = useState({});

  const [selectedIds, setSelectedIds] = useState([]);
  const [printImages, setPrintImages] = useState([]);
  const hiddenRef = useRef();

  const printRef = useRef();

  const toggleRow = (row) => {
    setSelectedIds((prev) =>
      prev.includes(row.id)
        ? prev.filter((id) => id !== row.id)
        : [...prev, row.id],
    );
  };
  const [globalFilter, setGlobalFilter] = useState("");

  const visibleIds = dataList.map((r) => r.id);
  const generateImages = async (rows) => {
    const imgs = [];

    for (const row of rows) {
      setEditData(row);

      // wait DOM update
      await new Promise((r) => requestAnimationFrame(r));

      if (hiddenRef.current) {
        const dataUrl = await htmlToImage.toPng(hiddenRef.current);
        imgs.push(dataUrl);
      }
    }

    setPrintImages(imgs);
  };

  const columns = useMemo(
    () => [
      {
        header: "Name",
        accessorKey: "name",
      },
      {
        header: "Category",
        accessorKey: "category",
      },
      {
        header: "Quantity",
        accessorKey: "quantity",
        cell: (info) => info.getValue() ?? 1,
      },
      {
        header: "Usage",
        accessorKey: "usage",
      },
      {
        header: "Assigned To",
        accessorKey: "employeeName",
      },
      {
        header: "Assigned Date",
        accessorKey: "assignedDate",
      },
    ],
    [],
  );

  const customGlobalFilterFn = (row, columnId, filterValue) => {
    if (!filterValue) return true;
    const searchTerms = String(filterValue).toLowerCase().trim().split(/\s+/);
    // Combine all row values into a single lowercase string for easy searching
    const rowValues = Object.values(row.original)
      .map((val) => String(val ?? ""))
      .join(" ")
      .toLowerCase();
    return searchTerms.every((term) => rowValues.includes(term));
  };

  const table = useReactTable({
    data: dataList,
    columns,
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: customGlobalFilterFn,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const selectCurrentPage = () => {
    const pageRows = table.getRowModel().rows;
    const ids = pageRows.map((r) => r.original.id);

    const allSelected = ids.every((id) => selectedIds.includes(id));

    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...ids])]);
    }
  };

  const selectAllFiltered = () => {
    const filteredRows = table.getFilteredRowModel().rows;
    const ids = filteredRows.map((r) => r.original.id);

    const allSelected = ids.every((id) => selectedIds.includes(id));

    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...ids])]);
    }
  };

  const filteredCount = table.getFilteredRowModel().rows.length;

  const selectedFilteredCount = table
    .getFilteredRowModel()
    .rows.filter((r) => selectedIds.includes(r.original.id)).length;

  const isAllFilteredSelected =
    filteredCount > 0 && filteredCount === selectedFilteredCount;

  const isIndeterminate =
    selectedFilteredCount > 0 && selectedFilteredCount < filteredCount;

  const handleSelectAll = () => {
    console.log("IT CAME");
    const allSelected = visibleIds.every((id) => selectedIds.includes(id));

    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...visibleIds])]);
    }
  };

  const rowsToPrint = dataList.filter((r) => selectedIds.includes(r.id));

  const handleRowClick = (e, row) => {
    // if user clicked checkbox → ignore
    if (e.target.type === "checkbox") return;

    toggleRow(row);
  };

  useEffect(() => {
    try {
      if (data?.getInventory?.inventoryList) {
        setDataList(data.getInventory.inventoryList);
        setIsFetched(true);
      }
    } catch (err) {
      console.log(err);
    }
  }, [data]);

  // Delete without reload: update local state on success
  const handleRemoveRow = async (dataId) => {
    try {
      const res = await deleteInventory({
        variables: { data: dataId },
      });
      if (res.data?.deleteInventory?.status === "SUCCESS") {
        setDataList((prev) => prev.filter((r) => r.id !== dataId));
      } else {
        console.error("Delete failed", res.data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Called when Edit button clicked
  const handleEditClick = (data) => {
    setEditData(data);
    setEditForm(true);
  };

  // Called by EditInventory after successful update to refresh one row locally
  const applyRowUpdate = (updatedRow) => {
    setDataList((prev) =>
      prev.map((r) => (r.id === updatedRow.id ? { ...r, ...updatedRow } : r)),
    );
  };

  const handlePrintSelected = async () => {
    if (!rowsToPrint.length) return;

    try {
      // optimistic update
      const updated = rowsToPrint.map((row) => ({
        ...row,
        printedCount: (row.printedCount || 0) + 1,
      }));

      setDataList((prev) =>
        prev.map((r) => updated.find((u) => u.id === r.id) || r),
      );

      // persist on server
      await Promise.all(
        updated.map((row) =>
          updateInventory({
            variables: {
              data: {
                id: row.id,
                usage: row.usage ? parseInt(row.usage) : 0,
                printedCount: row.printedCount,
                employeeId: row.employeeId,
              },
            },
          }),
        ),
      );

      window.print();

      // clear selection after print
      // setSelectedIds([]);
    } catch (err) {
      console.error("Print failed", err);
    }
  };

  // Print: optimistic increment printedCount, persist on server, rollback on error

  // const handlePrintSelected = async () => {
  //   if (!rowsToPrint.length) return;

  //   try {
  //     // optimistic update
  //     const updated = rowsToPrint.map((row) => ({
  //       ...row,
  //       printedCount: (row.printedCount || 0) + 1,
  //     }));

  //     setDataList((prev) =>
  //       prev.map((r) => updated.find((u) => u.id === r.id) || r)
  //     );

  //     // await Promise.all(
  //     //   updated.map((row) =>
  //     //     updateInventory({
  //     //       variables: {
  //     //         data: {
  //     //           id: row.id,
  //     //           usage: row.usage ? parseInt(row.usage) : 0,
  //     //           printedCount: row.printedCount,
  //     //           employeeId: row.employeeId,
  //     //         },
  //     //       },
  //     //     })
  //     //   )
  //     // );

  //     // 🔥 generate images
  //     await generateImages(updated);

  //     // small delay so DOM renders them
  //     setTimeout(() => {
  //       window.print();
  //     }, 300);
  //   } catch (err) {
  //     console.error(err);
  //   }
  // };

  const handleSaveAsImage = async (row) => {
    // create an updated copy (do not mutate original row)
    const updatedRow = { ...row, printedCount: (row.printedCount || 0) + 1 };

    // Optimistically update UI
    applyRowUpdate(updatedRow);

    // set label data so label reflects incremented count (if you render it)
    setEditData(updatedRow);

    // ensure DOM has updated so html-to-image captures correct content
    await new Promise((resolve) => requestAnimationFrame(resolve));

    // attempt server update
    try {
      await updateInventory({
        variables: {
          data: {
            id: updatedRow.id,
            usage: updatedRow.usage ? parseInt(updatedRow.usage) : 0,
            printedCount: updatedRow.printedCount,
            employeeId: updatedRow.employeeId,
          },
        },
      });
      // on success: proceed to capture image
    } catch (err) {
      console.error(
        "Failed to persist printedCount, rolling back local change",
        err,
      );
      // rollback UI change
      setDataList((prev) =>
        prev.map((r) =>
          r.id === row.id ? { ...r, printedCount: row.printedCount } : r,
        ),
      );
      // also update editData back
      setEditData(row);
      return; // abort image generation since server update failed
    }

    // generate image (only after server update succeeded)
    if (printRef.current) {
      try {
        const dataUrl = await htmlToImage.toPng(printRef.current);
        download(dataUrl, `${updatedRow.name}-label.png`);
      } catch (error) {
        console.error("Error generating image:", error);
      }
    }
  };

  if (loading || prodDeletLoading || !isFetched) return <Loader />;

  return (
    <>
      {isFetched &&
        (editForm ? (
          <EditInventory
            editFormData={editData}
            setEditForm={setEditForm}
            onRowUpdated={(updatedRow) => {
              applyRowUpdate(updatedRow);
              setEditForm(false);
            }}
          />
        ) : (
          <div className="min-h-screen flex">
            <Sidebar />
            <Layout>
              <div className="flex flex-col space-y-6 pt-8 ">
                <div className="flex justify-between items-center">
                  <div className="flex text-[#FBB033] items-center font-be-jost-pro text-xl font-bold space-x-3 pt-8 pl-5">
                    <Link to="/admin/services" className="...">
                      {" "}
                      <span> Services</span>{" "}
                    </Link>
                    <Link to="/admin/category" className="...">
                      {" "}
                      <span>Category</span>{" "}
                    </Link>
                    <Link to="/admin/sub-category" className="...">
                      {" "}
                      <span>Sub Category</span>{" "}
                    </Link>
                    <p className="... bg-white text-[#00395E]">Inventory</p>
                    <Link to="/admin/inventory-category-table" className="...">
                      {" "}
                      <span>Inventory Category</span>{" "}
                    </Link>
                    <Link
                      to="/admin/order-feedback-question-table"
                      className=" text-[#FBB034] hover:bg-white hover:text-[#00395E] rounded-sm cursor-pointer text-base px-2 py-1 font-semibold font-be-jost-pro text-center md:text-left"
                    >
                      <span>Feedback Questions</span>
                    </Link>
                  </div>
                  <div className="flex items-center">
                    {can("inventory.create") && (
                      <div>
                        <Link
                          to="/admin/inventory/create"
                          className="font-be-jost-pro font-medium text-sm border border-[#FBB033] text-[#FBB033] rounded-md px-5 py-2"
                        >
                          Add Inventory
                        </Link>
                      </div>
                    )}

                    <button
                      onClick={handlePrintSelected}
                      disabled={!selectedIds.length}
                      className="bg-[#FBB033] text-black px-4 py-2 rounded-md"
                    >
                      Print Selected ({selectedIds.length})
                    </button>
                  </div>
                </div>

                <div className="p-8 mt-6 rounded shadow bg-[#0c0b0b]">
                  {/* Search + Select Buttons */}
                  <div className="flex justify-between mb-4">
                    <input
                      value={globalFilter ?? ""}
                      onChange={(e) => setGlobalFilter(e.target.value)}
                      placeholder="Search..."
                      className="px-3 py-2 rounded text-black"
                    />

                    <div className="space-x-2">
                      <button
                        onClick={selectCurrentPage}
                        className="bg-yellow-500 px-3 py-2 rounded"
                      >
                        Select Page
                      </button>

                      <button
                        onClick={selectAllFiltered}
                        className="bg-green-500 px-3 py-2 rounded"
                      >
                        Select All (Filtered)
                      </button>
                    </div>
                  </div>

                  <table className="w-full text-white">
                    <thead>
                      <tr>
                        <th>
                          <input
                            type="checkbox"
                            checked={isAllFilteredSelected}
                            ref={(el) => {
                              if (el) el.indeterminate = isIndeterminate;
                            }}
                            onChange={selectAllFiltered}
                          />
                        </th>

                        {table.getHeaderGroups()[0].headers.map((header) => (
                          <th key={header.id} className="p-2">
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                          </th>
                        ))}

                        <th>Actions</th>
                      </tr>
                    </thead>

                    <tbody>
                      {table.getRowModel().rows.map((row) => (
                        <tr key={row.id} className="border-b border-gray-700">
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(row.original.id)}
                              onChange={() => toggleRow(row.original)}
                            />
                          </td>

                          {row.getVisibleCells().map((cell) => (
                            <td key={cell.id} className="p-2">
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )}
                            </td>
                          ))}

                          <td className="space-x-2">
                            <button
                              onClick={() => handleEditClick(row.original)}
                              className="bg-white text-black px-2 py-1 rounded"
                            >
                              Edit
                            </button>

                            <button
                              onClick={() => handleSaveAsImage(row.original)}
                              className="bg-white text-black px-2 py-1 rounded"
                            >
                              {row.original.printedCount
                                ? `Reprint (${row.original.printedCount})`
                                : "Print"}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Pagination */}
                  <div className="flex justify-between mt-4">
                    <button
                      onClick={() => table.previousPage()}
                      disabled={!table.getCanPreviousPage()}
                      className={`px-4 py-2 rounded-md font-medium transition
      ${
        table.getCanPreviousPage()
          ? "bg-[#FBB033] text-black hover:bg-yellow-400"
          : "bg-gray-600 text-gray-400 cursor-not-allowed"
      }`}
                    >
                      Previous
                    </button>

                    <span className="text-white">
                      Page {table.getState().pagination.pageIndex + 1} of{" "}
                      {table.getPageCount()}
                    </span>

                    <button
                      onClick={() => table.nextPage()}
                      disabled={!table.getCanNextPage()}
                      className={`px-4 py-2 rounded-md font-medium transition
      ${
        table.getCanNextPage()
          ? "bg-[#FBB033] text-black hover:bg-yellow-400"
          : "bg-gray-600 text-gray-400 cursor-not-allowed"
      }`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>

              <div className="print-area">
                {rowsToPrint.map((row) => (
                  <InventoryLabel
                    key={row.id}
                    logoUrl="https://api.barbera.in/uploads/1756537340509.jpg"
                    code={row.name}
                    description={row.description}
                  />
                ))}
              </div>

              {/* <div className="print-area">
                {printImages.map((src, i) => (
                  <img key={i} src={src} alt="label" />
                ))}
              </div> */}

              {/* <div style={{ position: "absolute", left: "-9999px" }}>
                <InventoryLabel
                  ref={hiddenRef}
                  logoUrl="https://api.barbera.in/uploads/1756537340509.jpg"
                  code={editData?.name}
                  description={editData?.description}
                />
              </div> */}

              {/* Hidden printable label */}
              <div style={{ position: "absolute", left: "-9999px" }}>
                <InventoryLabel2
                  ref={printRef}
                  logoUrl="https://api.barbera.in/uploads/1756537340509.jpg"
                  code={editData.name || ""}
                  description={editData.description || ""}
                />
              </div>
            </Layout>
          </div>
        ))}
    </>
  );
};

export default InventoryTable;
